import {
  BillingHeader,
  BillingKpis,
  HeaderParams,
  KpiParams,
  ListParams,
  PagedResponse,
  PagedResult,
  RiskArea,
} from "../types";
import { BULK_ID_PAGE_SIZE } from "../constants";
import {
  GroupFilterField,
  buildHeaderQuery,
  buildKpiQuery,
  buildListQuery,
} from "./query-builder";
import { callEnterpriseApi } from "./enterprise-api";

// The three v1 endpoints behind the Billing Risks tab. Plain promise-returning
// functions — ./risk-areas-api wraps each in an RTK Query endpoint, and
// status-counts-service calls fetchBillingHeaders directly.

const get = callEnterpriseApi;

// Normalizes the v1 PagedResponse envelope. The wire only carries totalItems,
// so hasNext is derived (page * pageSize < totalItems).
export const toPagedResult = (
  response: PagedResponse<RiskArea> | null,
  params: ListParams
): PagedResult<RiskArea> => {
  const page = response?.pagination?.page ?? params.page;
  const pageSize = response?.pagination?.pageSize ?? params.pageSize;
  const totalCount = response?.pagination?.totalItems ?? 0;
  return {
    items: response?.data ?? [],
    totalCount,
    page,
    pageSize,
    hasNext: page * pageSize < totalCount,
    facets: response?.facets ?? [],
  };
};

const fetchPage = async (
  path: string,
  params: ListParams,
  groupField: GroupFilterField
): Promise<PagedResult<RiskArea>> => {
  const response = (await get(
    `${path}?${buildListQuery(params, groupField)}`
  )) as PagedResponse<RiskArea>;
  return toPagedResult(response, params);
};

// Runaway guard for the id walk below: at BULK_ID_PAGE_SIZE this still covers
// 10k rows, so it only trips on a bad totalItems that keeps hasNext true forever.
const MAX_PAGES = 100;

// Shared list fetch for /billing, /providers and /coders — one call, one page.
// Accumulating pages is the caller's job: each accordion section owns an RTK
// Query infinite query whose cache entry holds its own pages.
export const fetchList = (
  path: string,
  params: ListParams,
  groupField: GroupFilterField = "ProviderGroup"
): Promise<PagedResult<RiskArea>> => fetchPage(path, params, groupField);

// Every matching row, walking the pages until the end. For the one caller that
// needs the whole result set: "Bulk Update N results" writes to every row
// behind the filters, and the legacy endpoint takes ids, not a filter.
//
// Returns whole rows, not just ids: the bulk update merges the form's
// filled-in fields over each row's current values, so ids alone would leave
// nothing to keep and blanks would write as nulls.
export const fetchAllRows = async (
  path: string,
  params: ListParams,
  groupField: GroupFilterField = "ProviderGroup"
): Promise<RiskArea[]> => {
  const first = await fetchPage(
    path,
    { ...params, page: 1, pageSize: BULK_ID_PAGE_SIZE, includeFacets: false },
    groupField
  );

  const rows = [...first.items];
  // Page size is read back off the response — the API clamps it — so the
  // offsets for the remaining pages match what the server actually used.
  const pageSize = first.pageSize || BULK_ID_PAGE_SIZE;
  let page = first.page;
  // Annotated because an inferred type would be the literal from `first`, which
  // the loop could not then assign the next page's flag back into.
  let hasNext: boolean = first.hasNext;

  while (hasNext && page < MAX_PAGES) {
    page += 1;
    const next = await fetchPage(
      path,
      { ...params, page, pageSize, includeFacets: false },
      groupField
    );
    if (!next.items.length) break;
    rows.push(...next.items);
    hasNext = next.hasNext;
  }

  return rows;
};

// 1b. Every billing row behind the current filters (bulk selection only).
export const fetchAllBillingRows = (params: ListParams): Promise<RiskArea[]> =>
  fetchAllRows("/api/v1/risk-areas/billing", params);

// 1. Paginated billing risk list.
export const fetchBillingRisks = (params: ListParams): Promise<PagedResult<RiskArea>> =>
  fetchList("/api/v1/risk-areas/billing", params);

// 2. Weight-bucket accordion headers (filter-aware). Also called directly by
// status-counts-service with groupBy=Status: the status-tab badge counts read
// these same grouped counts, so they get their own cache entry rather than
// sharing the accordion's.
export const fetchBillingHeaders = async (
  params: HeaderParams
): Promise<BillingHeader[]> => {
  const response = (await get(
    `/api/v1/risk-areas/billing/groups?${buildHeaderQuery(params)}`
  )) as BillingHeader[];
  return response ?? [];
};

// 3. Snapshot KPI cards (quarter only).
export const fetchBillingKpis = async (
  params: KpiParams
): Promise<BillingKpis | null> => {
  const response = (await get(
    `/api/v1/risk-areas/billing/kpis?${buildKpiQuery(params)}`
  )) as BillingKpis;
  return response ?? null;
};
